/**
 * FillFromFileDialog — compare AI-extracted values with current form values;
 * apply selected values to the open record form (client-side merge, user saves).
 */
import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import type { IntakeField, IntakeTabularPart, RefCandidate } from './ScanImportDialog'

export interface ExtractionProposal {
  status: string
  raw_text: string
  record: Record<string, unknown>
  tabular: Record<string, Record<string, unknown>[]>
  ref_candidates: Record<string, RefCandidate>
}

const isEmpty = (v: unknown) => v === null || v === undefined || v === ''

export function FillFromFileDialog({
  open,
  onClose,
  fields,
  tabularParts,
  proposal,
  currentData,
  onApply,
}: {
  open: boolean
  onClose: () => void
  fields: IntakeField[]
  tabularParts: IntakeTabularPart[]
  proposal: ExtractionProposal | null
  currentData: Record<string, unknown>
  onApply: (header: Record<string, unknown>, tabularAppend: Record<string, Record<string, unknown>[]>) => void
}) {
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [partChecked, setPartChecked] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!open || !proposal) return
    const init: Record<string, boolean> = {}
    for (const name of Object.keys(proposal.record)) {
      init[name] = isEmpty(currentData[name]) // overwrite is an explicit choice
    }
    setChecked(init)
    const partsInit: Record<string, boolean> = {}
    for (const name of Object.keys(proposal.tabular)) partsInit[name] = true
    setPartChecked(partsInit)
  }, [open, proposal, currentData])

  if (!proposal) return null

  const fieldTitle = (name: string) => fields.find((f) => f.name === name)?.title || name
  const partTitle = (name: string) => tabularParts.find((p) => p.name === name)?.title || name

  const handleApply = () => {
    const header: Record<string, unknown> = {}
    for (const [name, value] of Object.entries(proposal.record)) {
      if (checked[name]) header[name] = value
    }
    const tabularAppend: Record<string, Record<string, unknown>[]> = {}
    for (const [part, rows] of Object.entries(proposal.tabular)) {
      if (!partChecked[part]) continue
      tabularAppend[part] = rows.map((row) =>
        Object.fromEntries(Object.entries(row).filter(([k]) => !k.endsWith('__text'))),
      )
    }
    onApply(header, tabularAppend)
    onClose()
  }

  const unmatchedRefs = Object.entries(proposal.ref_candidates).filter(
    ([name, c]) => !c.resolved_id && !proposal.record[name],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Fill fields from file</DialogTitle>
      <DialogContent>
        {proposal.status !== 'ok' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            AI extraction is unavailable — no values were proposed. Recognized text is stored on the attachment.
          </Alert>
        )}
        {unmatchedRefs.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Unmatched references (pick manually on the form):{' '}
            {unmatchedRefs.map(([name, c]) => `${fieldTitle(name)}: "${c.text}"`).join(', ')}
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Current</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Proposed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(proposal.record).map(([name, value]) => (
              <TableRow key={name}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={!!checked[name]}
                    onChange={(e) => setChecked((prev) => ({ ...prev, [name]: e.target.checked }))}
                  />
                </TableCell>
                <TableCell>{fieldTitle(name)}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {isEmpty(currentData[name]) ? '—' : String(currentData[name])}
                </TableCell>
                <TableCell>{String(value)}</TableCell>
              </TableRow>
            ))}
            {Object.entries(proposal.tabular).map(([part, rows]) => (
              <TableRow key={part}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={!!partChecked[part]}
                    onChange={(e) => setPartChecked((prev) => ({ ...prev, [part]: e.target.checked }))}
                  />
                </TableCell>
                <TableCell colSpan={3}>
                  <Chip size="small" label={`Append ${rows.length} row${rows.length === 1 ? '' : 's'} to ${partTitle(part)}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            OCR text:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
            {proposal.raw_text || 'No text extracted'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={proposal.status !== 'ok'}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  )
}
